import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api.js';

const CORRIDOR_LABELS = {
  '16th_st': '16th Street',
  '24th_st': '24th Street',
  '32nd_st': '32nd Street',
  '40th_st': '40th Street',
  'mcdowell_rd': 'McDowell Road',
  'thomas_rd': 'Thomas Road',
  'indian_school_rd': 'Indian School Road',
  'camelback_rd': 'Camelback Road',
};

const CATEGORY_LABELS = {
  food_dining: 'Food & Dining',
  retail_shopping: 'Retail & Shopping',
  professional_services: 'Professional Services',
  health_wellness: 'Health & Wellness',
  financial_services: 'Financial Services',
  real_estate: 'Real Estate',
  automotive: 'Automotive',
  personal_services: 'Personal Services',
  construction: 'Construction',
  manufacturing: 'Manufacturing',
  hospitality: 'Hospitality',
  arts_entertainment: 'Arts & Entertainment',
  education: 'Education',
  nonprofit: 'Nonprofit',
  wholesale: 'Wholesale',
  transportation: 'Transportation',
  communications: 'Communications',
  utilities: 'Utilities',
  government: 'Government',
  agriculture: 'Agriculture',
  other: 'Other',
};

const REVENUE_TIERS = [
  { label: 'All revenue', min: '', max: '' },
  { label: 'Under $500K', min: '', max: 500000 },
  { label: '$500K - $1M', min: 500000, max: 1000000 },
  { label: '$1M - $2.5M', min: 1000000, max: 2500000 },
  { label: '$2.5M - $5M', min: 2500000, max: 5000000 },
  { label: '$5M - $10M', min: 5000000, max: 10000000 },
  { label: '$10M+', min: 10000000, max: '' },
];

const STATUS_FILTERS = [
  { value: '', label: 'All prospects' },
  { value: 'identified', label: 'Identified' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'interested', label: 'Interested' },
  { value: 'committed', label: 'Committed' },
  { value: 'declined', label: 'Declined' },
];

export default function FilterSidebar({ filters, onChange, campaignId }) {
  const { data: stats } = useQuery({
    queryKey: ['stats', campaignId],
    queryFn: () => api.getStats(campaignId),
    enabled: !!campaignId,
  });

  function set(field, value) {
    onChange({ ...filters, [field]: value });
  }

  function setRevenue(idx) {
    const tier = REVENUE_TIERS[idx];
    onChange({ ...filters, min_revenue: tier.min, max_revenue: tier.max });
  }

  return (
    <aside className="w-72 bg-white border-r border-gray-200 overflow-y-auto">
      <div className="p-4 space-y-5">
        {/* Search */}
        <div>
          <label className="block text-xs font-semibold text-gray-700 uppercase mb-1">
            Search
          </label>
          <input
            type="text"
            value={filters.q || ''}
            onChange={(e) => set('q', e.target.value)}
            placeholder="Name or category"
            className="w-full text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-ccf-primary"
          />
        </div>

        {/* Corridor */}
        <div>
          <label className="block text-xs font-semibold text-gray-700 uppercase mb-1">
            Corridor
          </label>
          <select
            value={filters.corridor || ''}
            onChange={(e) => set('corridor', e.target.value)}
            className="w-full text-sm border border-gray-300 rounded px-2 py-1"
          >
            <option value="">All corridors</option>
            {Object.entries(CORRIDOR_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label} {stats?.corridors?.find(c => c.corridor === value)?.n && `(${stats.corridors.find(c => c.corridor === value).n})`}
              </option>
            ))}
          </select>
        </div>

        {/* Category */}
        <div>
          <label className="block text-xs font-semibold text-gray-700 uppercase mb-1">
            Category
          </label>
          <select
            value={filters.category || ''}
            onChange={(e) => set('category', e.target.value)}
            className="w-full text-sm border border-gray-300 rounded px-2 py-1"
          >
            <option value="">All categories</option>
            {(stats?.categories || []).map((c) => (
              <option key={c.category} value={c.category}>
                {CATEGORY_LABELS[c.category] || c.category} ({c.n})
              </option>
            ))}
          </select>
        </div>

        {/* Revenue tier */}
        <div>
          <label className="block text-xs font-semibold text-gray-700 uppercase mb-1">
            Revenue
          </label>
          <select
            value={(() => {
              const idx = REVENUE_TIERS.findIndex(
                (t) => t.min == (filters.min_revenue || '') && t.max == (filters.max_revenue || '')
              );
              return idx === -1 ? 0 : idx;
            })()}
            onChange={(e) => setRevenue(parseInt(e.target.value))}
            className="w-full text-sm border border-gray-300 rounded px-2 py-1"
          >
            {REVENUE_TIERS.map((t, i) => (
              <option key={i} value={i}>
                {t.label}
              </option>
            ))}
          </select>
        </div>

        {/* Prospect status (only if campaign selected) */}
        {campaignId && (
          <div>
            <label className="block text-xs font-semibold text-gray-700 uppercase mb-1">
              Prospect Status
            </label>
            <select
              value={filters.status || ''}
              onChange={(e) => set('status', e.target.value)}
              className="w-full text-sm border border-gray-300 rounded px-2 py-1"
            >
              {STATUS_FILTERS.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Totals */}
        {stats && (
          <div className="pt-3 border-t border-gray-200 text-xs text-gray-600">
            <div>District total: <span className="font-semibold">{stats.total}</span> businesses</div>
          </div>
        )}

        {/* Reset */}
        <button
          onClick={() => onChange({})}
          className="w-full text-xs text-gray-600 hover:text-gray-900 underline"
        >
          Reset filters
        </button>
      </div>
    </aside>
  );
}
